import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Sidebar } from "../components/Sidebar";

// Configuration du service d'images
// - En production : utilise /api-images (proxy NGINX qui ajoute le token automatiquement)
// - En développement : utilise REACT_APP_IMAGE_SERVICE_URL ou /api-images si non défini
// Le token est géré différemment selon l'environnement :
//   - Production : ajouté automatiquement par NGINX du VPS
//   - Développement : via REACT_APP_IMAGE_API_TOKEN si défini
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_SERVICE_URL || "/api-images";
const IMAGE_API_TOKEN = process.env.REACT_APP_IMAGE_API_TOKEN || "";

// Détecter si on est en développement local (pas de proxy NGINX)
const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

const CDN_LOCAL_BASE = "http://localhost:8090/images";
const CDN_STAGING_BASE = "https://cdn-staging.luniwave.com/images";
const CDN_PROD_BASE = "https://cdn.luniwave.com/images";

const getMappedCdnBaseUrl = () => {
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") return CDN_LOCAL_BASE;
  if (host.includes("preprod") || host.includes("staging")) return CDN_STAGING_BASE;
  return CDN_PROD_BASE;
};

const buildImageUrl = (filename) => `${getMappedCdnBaseUrl()}/${encodeURIComponent(filename)}`;

function ImagesPage() {
  const [file, setFile] = useState(null);
  const [customName, setCustomName] = useState("");
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState("info");
  const [files, setFiles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilePreview, setSelectedFilePreview] = useState(null);
  const [failedImages, setFailedImages] = useState({});

  // Gestion du token selon l'environnement
  // - En local : utilise le token de la variable d'environnement si défini
  // - En production : NGINX ajoute le token automatiquement, pas besoin de le passer
  const apiHeaders = useMemo(() => {
    // En développement local, utiliser le token si disponible
    if (isLocalDev && IMAGE_API_TOKEN) {
      return { "x-api-token": IMAGE_API_TOKEN };
    }
    // En production, NGINX ajoute le token automatiquement
    return {};
  }, []);

  const refreshUploads = useCallback(async () => {
    setLoadingList(true);
    setStatus(""); // Réinitialiser le statut
    try {
      const url = `${IMAGE_SERVICE_URL}/uploads`;
      
      const res = await fetch(url, {
        headers: apiHeaders,
      });
      
      // Vérifier le Content-Type avant de parser en JSON
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error("❌ [ImagesPage] Réponse non-JSON:", text.substring(0, 200));
        throw new Error(`Réponse non-JSON reçue (${res.status}): ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || data.message || "Erreur lecture des uploads");
      }
      
      const filesList = data.files || [];
      setFiles(filesList);
    } catch (err) {
      console.error("❌ [ImagesPage] Erreur lors du chargement des images:", err);
      setStatus(err.message || "Erreur lecture des uploads");
    } finally {
      setLoadingList(false);
    }
  }, [apiHeaders]);

  React.useEffect(() => {
    refreshUploads();
  }, [refreshUploads]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setStatusType("info");
    setStatus("Upload en cours...");

    const formData = new FormData();
    formData.append("image", file);
    if (customName.trim()) {
      formData.append("name", customName.trim());
    }

    try {
      const res = await fetch(`${IMAGE_SERVICE_URL}/upload`, {
        method: "POST",
        headers: apiHeaders,
        body: formData,
      });
      
      // Vérifier le Content-Type avant de parser en JSON
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(`Réponse non-JSON reçue (${res.status}): ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Erreur upload");
      }
      setStatusType("success");
      setStatus("Upload réussi");
      setFile(null);
      setCustomName("");
      await refreshUploads();
    } catch (err) {
      console.error("Erreur lors de l'upload:", err);
      setStatus(err.message || "Erreur upload");
    }
  };

  const handleRename = async (oldFilename) => {
    const newName = window.prompt("Nouveau nom (sans extension):", "");
    if (!newName) return;

    try {
      const res = await fetch(`${IMAGE_SERVICE_URL}/rename`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...apiHeaders,
        },
        body: JSON.stringify({ oldFilename, newName }),
      });
      
      // Vérifier le Content-Type avant de parser en JSON
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(`Réponse non-JSON reçue (${res.status}): ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Erreur renommage");
      }
      setStatusType("success");
      setStatus("Renommage réussi");
      await refreshUploads();
    } catch (err) {
      console.error("Erreur lors du renommage:", err);
      setStatus(err.message || "Erreur renommage");
    }
  };

  const imageFiles = useMemo(() => {
    return files.filter((filename) => /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(filename));
  }, [files]);

  const visibleFiles = useMemo(() => {
    if (!searchTerm.trim()) return imageFiles;
    return imageFiles.filter((filename) => filename.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  }, [imageFiles, searchTerm]);

  const nonImageCount = useMemo(() => {
    return files.length - imageFiles.length;
  }, [files.length, imageFiles.length]);

  useEffect(() => {
    if (!file) {
      setSelectedFilePreview(null);
      return undefined;
    }
    const previewUrl = URL.createObjectURL(file);
    setSelectedFilePreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [file]);

  const handleCopyImageUrl = async (filename) => {
    const url = buildImageUrl(filename);
    try {
      await navigator.clipboard.writeText(url);
      setStatusType("success");
      setStatus("URL copiée dans le presse-papiers");
    } catch (err) {
      setStatusType("error");
      setStatus("Impossible de copier l'URL");
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <h1 className="text-2xl font-bold text-[#365644] mb-2">Images</h1>
        <p className="text-sm text-gray-600 mb-6">
          Service cible: <span className="font-mono">{IMAGE_SERVICE_URL}</span>
          <br />
          <span className="text-xs text-gray-500">{isLocalDev ? (IMAGE_API_TOKEN ? "Mode développement : token utilisé depuis REACT_APP_IMAGE_API_TOKEN" : "Mode développement : aucun token configuré (configurez REACT_APP_IMAGE_SERVICE_URL et REACT_APP_IMAGE_API_TOKEN)") : "Mode production : token géré automatiquement par le proxy NGINX"}</span>
        </p>

        {status && (
          <p className={`mb-4 text-sm ${statusType === "error" ? "text-red-600" : statusType === "success" ? "text-green-700" : "text-gray-700"}`}>
            {status}
          </p>
        )}

        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <h2 className="text-lg font-semibold text-[#365644] mb-1">Uploader une image</h2>
            <p className="text-xs text-gray-500 mb-4">Formats supportés: PNG, JPG, WEBP, GIF.</p>

            <form onSubmit={handleUpload} className="grid gap-3 md:grid-cols-[1.2fr,1fr,auto] md:items-end">
              <div>
                <label htmlFor="image-file" className="block text-sm font-medium text-gray-700 mb-1">
                  Fichier image
                </label>
                <input id="image-file" type="file" accept="image/*" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-[#365644] file:px-3 file:py-2 file:text-white hover:file:bg-[#2d4c3c]" />
              </div>

              <div>
                <label htmlFor="custom-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom personnalisé (optionnel)
                </label>
                <input id="custom-name" type="text" value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Ex : Produit locaux" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>

              <button type="submit" disabled={!file} className="bg-[#365644] text-white px-5 py-2 rounded-md hover:bg-[#2d4c3c] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap">
                Uploader
              </button>
            </form>

            {file && (
              <div className="mt-4 p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3">
                {selectedFilePreview && <img src={selectedFilePreview} alt="Aperçu du fichier sélectionné" className="w-16 h-16 object-cover rounded bg-white border border-gray-200" />}
                <div>
                  <p className="text-xs text-gray-600">Fichier sélectionné</p>
                  <p className="text-sm text-gray-800 break-all">{file.name}</p>
                </div>
              </div>
            )}
        </section>

        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-[#365644]">Bibliothèque d'images</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                  {visibleFiles.length}/{imageFiles.length} images
                </span>
                <button type="button" onClick={refreshUploads} className="text-sm underline text-[#365644]">
                  Rafraichir
                </button>
              </div>
            </div>

            <div className="mb-4">
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Rechercher un fichier..." className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>

            {loadingList ? (
              <p className="text-sm text-gray-500">Chargement...</p>
            ) : visibleFiles.length === 0 ? (
              <p className="text-sm text-gray-500">{imageFiles.length === 0 ? "Aucune image." : "Aucun resultat pour cette recherche."}</p>
            ) : (
              <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
                {visibleFiles.map((filename) => {
                  const url = buildImageUrl(filename);
                  return (
                    <article key={filename} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="aspect-[4/3] rounded-md overflow-hidden bg-gray-100 mb-2">
                        {failedImages[filename] ? (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">Aperçu indisponible</div>
                        ) : (
                          <img
                            src={url}
                            alt={filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={() => setFailedImages((prev) => ({ ...prev, [filename]: true }))}
                          />
                        )}
                      </div>
                      <p className="text-xs break-all mb-3 min-h-[2rem]">{filename}</p>
                      <div className="flex flex-wrap gap-2">
                        <a href={url} target="_blank" rel="noreferrer" className="text-sm border border-gray-300 rounded px-2 py-1 hover:bg-gray-50">
                          Voir
                        </a>
                        <button type="button" className="text-sm border border-gray-300 rounded px-2 py-1 hover:bg-gray-50" onClick={() => handleCopyImageUrl(filename)}>
                          Copier URL
                        </button>
                        <button type="button" className="text-sm border border-gray-300 rounded px-2 py-1 hover:bg-gray-50" onClick={() => handleRename(filename)}>
                          Renommer
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
        </section>

      </div>
    </div>
  );
}

export default ImagesPage;
